/**
 * Shared eEB kiosk HTTP contracts.
 *
 * These schemas are the one decode boundary on both sides of the dedicated `_rest_`
 * endpoint. The mutation deliberately has no tenant, practice, mailbox, actor, patient
 * demographics or lifecycle-state fields.
 *
 * @since 1.0.0
 */

import { Schema as S } from "effect";

/**
 * Versioned consent wording accepted by this release.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { EebConsentTextVersion } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * const version = EebConsentTextVersion.literals[0];
 * ```
 */
export const EebConsentTextVersion = S.Literals(
  ["eeb-consent-de-2-staff",
  "eeb-consent-de-2-automatic"],
);

/**
 * Submission behavior the patient is authorizing for this request.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { EebSubmissionMode } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * const modes = EebSubmissionMode.literals;
 * ```
 */
export const EebSubmissionMode = S.Literals(["staff-mediated", "automatic"]);

/**
 * Version of the strict statutory-exception declaration shown before consent.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { EebExceptionDeclarationVersion } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * const version = EebExceptionDeclarationVersion.literals[0];
 * ```
 */
export const EebExceptionDeclarationVersion = S.Literal("eeb-exception-de-1");

/**
 * Minimal durable-consent mutation sent by Frontdesk+.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema as S } from "effect";
 * import { EebKioskConsentRequest } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * const decode = S.decodeUnknownSync(EebKioskConsentRequest);
 * const request = decode({
 *   patientId: "6a6353969381cb04c924b97e",
 *   clientSubmissionId: "1f311c40-fee9-4b03-b0c4-c29d432f2371",
 *   presentedConfigVersion: 4,
 *   presentedConfiguredMode: "staff-mediated",
 *   consentTextVersion: "eeb-consent-de-2-staff",
 *   consentTextHash: "a".repeat(64),
 *   exceptionDeclarationVersion: "eeb-exception-de-1",
 *   exceptionDeclarationHash: "b".repeat(64),
 *   exceptionConfirmed: true,
 * });
 * ```
 */
export class EebKioskConsentRequest extends S.Class<EebKioskConsentRequest>(
  "EebKioskConsentRequest",
)({
  patientId: S.NonEmptyString,
  clientSubmissionId: S.String.check(S.isUUID()),
  presentedConfigVersion: S.Int.pipe(S.check(S.isGreaterThan(0))),
  presentedConfiguredMode: EebSubmissionMode,
  consentTextVersion: EebConsentTextVersion,
  consentTextHash: S.String.pipe(S.check(S.isPattern(/^[0-9a-f]{64}$/))),
  exceptionDeclarationVersion: EebExceptionDeclarationVersion,
  exceptionDeclarationHash: S.String.pipe(S.check(S.isPattern(/^[0-9a-f]{64}$/))),
  exceptionConfirmed: S.Boolean,
}) {}

/**
 * Server-authoritative wording and practice display facts shown before consent.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema as S } from "effect";
 * import { EebConsentPresentation } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * const decode = S.decodeUnknownSync(EebConsentPresentation);
 * declare const payload: unknown;
 * const presentation = decode(payload);
 * ```
 */
export class EebConsentPresentation extends S.Class<EebConsentPresentation>(
  "EebConsentPresentation",
)({
  presentedConfigVersion: S.Int.pipe(S.check(S.isGreaterThan(0))),
  presentedConfiguredMode: EebSubmissionMode,
  consentTextVersion: EebConsentTextVersion,
  consentText: S.NonEmptyString,
  consentTextHash: S.String.pipe(S.check(S.isPattern(/^[0-9a-f]{64}$/))),
  submissionMode: EebSubmissionMode,
  consentActionLabel: S.NonEmptyString,
  exceptionDeclarationVersion: EebExceptionDeclarationVersion,
  exceptionDeclarationText: S.NonEmptyString,
  exceptionDeclarationHash: S.String.pipe(S.check(S.isPattern(/^[0-9a-f]{64}$/))),
  practiceName: S.NonEmptyString,
  practiceKimAddress: S.NonEmptyString,
  insurerName: S.NonEmptyString,
}) {}

/**
 * Every durable state a replay may truthfully report.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { EebKioskRequestState } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * const states = EebKioskRequestState.literals;
 * ```
 */
export const EebKioskRequestState = S.Literals(
  ["consented",
  "awaiting-staff-send",
  "preparing",
  "signed",
  "submitted",
  "retry-scheduled",
  "needs-staff-action",
  "readiness-blocked",
  "awaiting-response",
  "replied",
  "timed-out",
  "rejected",
  "failed",
  "manual-resolution",
  "expired",
  "cancelled"],
);

/**
 * Safe durable acceptance returned to Frontdesk+.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema as S } from "effect";
 * import { EebKioskConsentAcceptance } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * const decode = S.decodeUnknownSync(EebKioskConsentAcceptance);
 * ```
 */
export class EebKioskConsentAcceptance extends S.Class<EebKioskConsentAcceptance>(
  "EebKioskConsentAcceptance",
)({
  kind: S.Literals(["accepted", "replayed", "already-pending"]),
  submissionId: S.String.check(S.isUUID()),
  state: EebKioskRequestState,
  submissionMode: EebSubmissionMode,
  acceptedAt: S.String,
}) {}

/**
 * Redacted problem response. It carries no thrown object or request echo.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema as S } from "effect";
 * import { EebKioskProblem } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * const decode = S.decodeUnknownSync(EebKioskProblem);
 * ```
 */
export class EebKioskProblem extends S.Class<EebKioskProblem>(
  "EebKioskProblem",
)({
  problem: S.Literals(
    ["malformed-request",
    "unsupported-consent-version",
    "stale-consent-presentation",
    "no-active-config",
    "patient-ineligible",
    "exception-not-declared",
    "replay-conflict",
    "unavailable"],
  ),
  title: S.NonEmptyString,
}) {}

/**
 * Whether the server can accept an eEB consent right now.
 *
 * Carries a boolean and nothing else: the kiosk needs it to decide whether to
 * offer the eEB path, and a patient must not be able to read the practice's
 * configuration state off a kiosk screen.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * import * as S from "effect/Schema";
 * import { EebAvailability } from "@ascleo/frontdesk-contracts/eeb/kiosk";
 *
 * S.decodeUnknownSync(EebAvailability)({ available: false });
 * ```
 */
export class EebAvailability extends S.Class<EebAvailability>(
  "EebAvailability",
)({
  available: S.Boolean,
}) {}
